'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Account, ChatMessage, Contact } from '@/types'
import { displayName } from '@/lib/utils'
import { subscribeAccountMessages } from '@/lib/account-message-stream'

type UnreadCounts = Record<string, number>

const STORAGE_PREFIX = 'wxproto-desk-unread-counts:'
function readUnreadCounts(wxid: string): UnreadCounts {
  try {
    const value = window.localStorage.getItem(STORAGE_PREFIX + wxid)
    return value ? (JSON.parse(value) as UnreadCounts) : {}
  } catch {
    return {}
  }
}

function notificationBody(message: ChatMessage): string {
  return message.content.length > 120 ? message.content.slice(0, 120) + '...' : message.content
}

export function useMessageNotifications({
  account,
  contacts,
  messageNotifications,
  activeConversationWxid,
  conversationPageActive,
  onOpenConversation,
}: {
  account: Account | null
  contacts: Contact[]
  messageNotifications: boolean
  activeConversationWxid: string
  conversationPageActive: boolean
  onOpenConversation: (wxid: string) => void
}) {
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({})
  const skipNextPersistRef = useRef(false)
  const stateRef = useRef({
    activeConversationWxid,
    conversationPageActive,
    messageNotifications,
    contacts,
    onOpenConversation,
  })

  useEffect(() => {
    stateRef.current = {
      activeConversationWxid,
      conversationPageActive,
      messageNotifications,
      contacts,
      onOpenConversation,
    }
  }, [activeConversationWxid, contacts, conversationPageActive, messageNotifications, onOpenConversation])

  useEffect(() => {
    skipNextPersistRef.current = true
    setUnreadCounts(account ? readUnreadCounts(account.wxid) : {})
  }, [account])

  useEffect(() => {
    if (!account) return
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false
      return
    }
    window.localStorage.setItem(STORAGE_PREFIX + account.wxid, JSON.stringify(unreadCounts))
  }, [account, unreadCounts])

  const markConversationRead = useCallback((conversationWxid: string) => {
    if (!conversationWxid) return
    setUnreadCounts((current) => {
      if (!current[conversationWxid]) return current
      const next = { ...current }
      delete next[conversationWxid]
      return next
    })
  }, [])

  useEffect(() => {
    if (!conversationPageActive || !activeConversationWxid) return
    markConversationRead(activeConversationWxid)
  }, [activeConversationWxid, conversationPageActive, markConversationRead])

  useEffect(() => {
    if (!account) return
    return subscribeAccountMessages(account.wxid, (incoming) => {
      if (incoming.direction !== 'in') return

      const current = stateRef.current
      const isOpenConversation =
        current.conversationPageActive && current.activeConversationWxid === incoming.conversationWxid
      if (!isOpenConversation) {
        setUnreadCounts((counts) => ({
          ...counts,
          [incoming.conversationWxid]: (counts[incoming.conversationWxid] || 0) + 1,
        }))
      }

      if (current.messageNotifications && 'Notification' in window && Notification.permission === 'granted') {
        const contact = current.contacts.find((item) => item.wxid === incoming.conversationWxid)
        const title = contact ? displayName(contact) : incoming.conversationWxid
        const notification = new Notification(title || '微信消息', {
          body: notificationBody(incoming),
          icon: contact?.avatar,
        })
        notification.onclick = () => {
          window.focus()
          current.onOpenConversation(incoming.conversationWxid)
          notification.close()
        }
      }
    })
  }, [account])

  const totalUnread = useMemo(
    () => Object.values(unreadCounts).reduce((total, count) => total + count, 0),
    [unreadCounts],
  )

  return { unreadCounts, totalUnread, markConversationRead }
}
